import React, { useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CircularProgress from "@mui/material/CircularProgress";

import isEqual from "lodash/isEqual";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import useAuth from "../../../hooks/useAuth";

function locationsSnapshot(locations) {
  return locations.map(v => ({
    warehouse_location_ids: (v.warehouse_locations || []).map(loc => loc.id).sort().join(","),
    shipment_id: v.shipment_id || "",
    restock_sub_qty: v.restock_sub_qty || 0,
    mobile_warehouse_location_id: v.mobile_warehouse_location?.id,
  }));
}

const styles = {
  submitButton: {
    mr: "1px",
    height: 36,
  },
  dropdownButton: {
    px: 0,
    height: 36,
    minWidth: 40,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
};

export default function InventoryTransactionActions({
  loading,
  initialValues,
  restockAddToCart,
  displayBulkActions,
  onToggleMultiSelect,
  submitLabel,
  secondaryAction,
}) {
  const { values, submitForm } = useFormikContext();
  const { user } = useAuth();
  const { settings } = user.company;
  const isSimpleInventoryModal = settings.simple_inventory_modal;
  const [isShiftHeld, setIsShiftHeld] = useState(false);

  const hasBeenModified = useRef(false);
  useEffect(() => {
    if (!hasBeenModified.current && !isEqual(
      locationsSnapshot(initialValues.locations),
      locationsSnapshot(values.locations)
    )) {
      hasBeenModified.current = true;
    }
  }, [values.locations, initialValues.locations]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === "Shift") {
        setIsShiftHeld(true);
      }
    };

    const handleKeyUp = event => {
      if (event.key === "Shift") {
        setIsShiftHeld(false);
      }
    };

    const handleWindowBlur = () => {
      setIsShiftHeld(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  const emptyCurrentQuantity =
    isSimpleInventoryModal &&
    values.locations.some(v => v.sub_qty + (v.restock_sub_qty || 0) === 0);

  const emptyRackLocation = values.locations.some(
    v => !v.warehouse_locations?.length
  );

  const hasRequiredInfo = !emptyCurrentQuantity && !emptyRackLocation;
  const allowShiftOverride = isShiftHeld && hasRequiredInfo;
  const disabled =
    loading || (!hasBeenModified.current && !allowShiftOverride) || !hasRequiredInfo;

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      {displayBulkActions && isSimpleInventoryModal ? (
        <Button variant="text" color="primary" onClick={onToggleMultiSelect}>
          Bulk Actions
        </Button>
      ) : (
        <div />
      )}
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
        {secondaryAction}
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          <Button
            disabled={disabled}
            variant="contained"
            color="primary"
            onClick={submitForm}
            sx={{
              ...styles.submitButton,
              ...(isSimpleInventoryModal
                ? {}
                : { borderTopRightRadius: 0, borderBottomRightRadius: 0 }),
            }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitLabel}
          </Button>
          {!isSimpleInventoryModal && (
            <Box variant="contained" aria-label="split button">
              <PopupState variant="popover" popupId="demo-popup-menu">
                {popupState => (
                  <React.Fragment>
                    <Button
                      disabled={disabled}
                      variant="contained"
                      color="primary"
                      {...bindTrigger(popupState)}
                      sx={styles.dropdownButton}
                    >
                      <KeyboardArrowDownIcon
                        sx={{ transform: popupState.open ? undefined : "rotate(180deg)" }}
                      />
                    </Button>
                    <Menu {...bindMenu(popupState)}>
                      <MenuItem
                        onClick={() => {
                          restockAddToCart(values);
                          popupState.close();
                        }}
                      >
                        Submit + Add to Cart
                      </MenuItem>
                    </Menu>
                  </React.Fragment>
                )}
              </PopupState>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
